import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BookOpen, ClipboardCheck, Settings, ArrowRight, Copy, Check, RefreshCw, Users, CreditCard, Share2, Lock, KeyRound, UsersRound } from "lucide-react";

export function SchoolAdminDashboard({ school, pendingCount, copied, regenerating, onCopy, onRegenerate, hasActiveSub = false }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8 items-start">
      {/* Primary: day-to-day admin tools */}
      <div className="lg:col-span-2 border border-border rounded-2xl p-5">
        <p className="text-[0.75rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-4">
          Admin Tools
        </p>

        <div className="grid grid-cols-1 gap-3">
          <Card as={Link} hoverable to="/schools/report-folders" className="group flex items-center gap-4 px-5 py-[1.125rem] no-underline text-foreground">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><BookOpen size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Folder Ownership</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem] flex items-center gap-1.5">
                {!hasActiveSub && <Lock size={14} className="text-muted-foreground shrink-0" />}
                {hasActiveSub ? "Reassign Report Folders between teachers" : "School Plan required"}
              </div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Card>

          <Card as={Link} hoverable to="/schools/activity" className="group flex items-center gap-4 px-5 py-[1.125rem] no-underline text-foreground">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Users size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">School Activity</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem] flex items-center gap-1.5">
                {!hasActiveSub && <Lock size={14} className="text-muted-foreground shrink-0" />}
                {hasActiveSub ? "Usage charts, trends and teacher breakdown" : "School Plan required"}
              </div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Card>

          <Card as={Link} hoverable to="/schools/report-folder-shares" className="group flex items-center gap-4 px-5 py-[1.125rem] no-underline text-foreground">
            <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0"><Share2 size={20} /></div>
            <div>
              <div className="text-[0.9375rem] font-semibold">Active Folder Sharing</div>
              <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">Read-only audit of who has access to which folders</div>
            </div>
            <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
          </Card>

          {school?.sharingMode === "restricted" && (
            <Card as={Link} hoverable to="/schools/share-requests" className="group flex items-center gap-4 px-5 py-[1.125rem] no-underline text-foreground">
              <div className="w-[42px] h-[42px] rounded-[10px] bg-accent flex items-center justify-center text-primary shrink-0" style={{ position: "relative" }}>
                <ClipboardCheck size={20} />
                {pendingCount > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    width: 16, height: 16, borderRadius: "50%",
                    background: "hsl(var(--destructive))",
                    color: "hsl(var(--destructive-foreground))",
                    fontSize: 9, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </div>
              <div>
                <div className="text-[0.9375rem] font-semibold">Sharing Approvals</div>
                <div className="text-[0.8125rem] text-muted-foreground mt-[0.15rem]">
                  {pendingCount > 0
                    ? `${pendingCount} pending approval${pendingCount !== 1 ? "s" : ""}`
                    : "Approve teacher share requests"}
                </div>
              </div>
              <ArrowRight size={15} className="ml-auto text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[3px] group-hover:text-primary" />
            </Card>
          )}
        </div>
      </div>

      {/* Secondary: school setup/management */}
      <aside className="bg-muted/30 border border-border rounded-2xl p-5">
        <p className="text-[0.75rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground mb-3">
          Manage School
        </p>

        <div className="divide-y divide-border/70">
          <Link
            to="/schools/seats"
            className="group flex items-center gap-3 py-3 no-underline text-foreground"
          >
            <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center text-primary shrink-0">
              <CreditCard size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.875rem] font-semibold leading-tight">School Plan</div>
              <div className="text-[0.75rem] text-muted-foreground mt-0.5">Buy and assign seats</div>
            </div>
            <ArrowRight size={13} className="text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[2px] group-hover:text-primary" />
          </Link>

          <Link
            to="/schools/members"
            className="group flex items-center gap-3 py-3 no-underline text-foreground"
          >
            <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center text-primary shrink-0">
              <UsersRound size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.875rem] font-semibold leading-tight">Members</div>
              <div className="text-[0.75rem] text-muted-foreground mt-0.5">View and remove teachers</div>
            </div>
            <ArrowRight size={13} className="text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[2px] group-hover:text-primary" />
          </Link>

          <Link
            to="/schools/settings"
            className="group flex items-center gap-3 py-3 no-underline text-foreground"
          >
            <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center text-primary shrink-0">
              <Settings size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.875rem] font-semibold leading-tight">School Settings</div>
              <div className="text-[0.75rem] text-muted-foreground mt-0.5">Name, sharing policy & more</div>
            </div>
            <ArrowRight size={13} className="text-muted-foreground shrink-0 transition-transform group-hover:translate-x-[2px] group-hover:text-primary" />
          </Link>

          {school?.inviteCode && (
            <div className="pt-3">
              <div className="flex items-center gap-3 mb-2.5">
                <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center text-primary shrink-0">
                  <KeyRound size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[0.875rem] font-semibold leading-tight">Invite Code</div>
                  <div className="text-[0.75rem] text-muted-foreground mt-0.5">Share with teachers to join</div>
                </div>
              </div>
              <code className="block text-sm font-mono tracking-widest bg-background border border-border px-2.5 py-1.5 rounded-lg select-all text-center mb-2">
                {school.inviteCode}
              </code>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="flex-1 gap-1.5 h-7 text-xs" onClick={onCopy}>
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button
                  variant="ghost" size="sm"
                  className="flex-1 h-7 gap-1.5 text-muted-foreground hover:text-foreground text-xs"
                  onClick={onRegenerate} disabled={regenerating}
                >
                  <RefreshCw size={11} className={regenerating ? "animate-spin" : ""} />
                  {regenerating ? "..." : "Regenerate"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
